class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    const form = this.querySelector('form');
    if (!form) return;
    form.addEventListener('input', event => {
      // Invalidate the previous response immediately, including the debounce interval.
      FacetFiltersForm.cancelPending();
      FacetFiltersForm.setLoading(true);
      FacetFiltersForm.submitTimer = setTimeout(() => this.onSubmitHandler(event), 250);
    });
    form.addEventListener('submit', event => this.onSubmitHandler(event));
    this.addEventListener('click', event => {
      if (event.target.closest('[data-qf-retry]') && event.target.closest('facet-filters-form') === this) {
        event.preventDefault();
        FacetFiltersForm.renderPage(FacetFiltersForm.lastAttempt ?? window.location.search.slice(1));
      }
    });
  }

  static cancelPending() {
    clearTimeout(this.submitTimer);
    this.controller?.abort();
    this.requestId = (this.requestId || 0) + 1;
  }

  static setListeners() {
    window.addEventListener('popstate', () => {
      const params = window.location.search.slice(1);
      if (params !== this.searchParamsPrev) this.renderPage(params, null, false);
    });
  }

  static setLoading(loading) {
    document.querySelector('#ProductGridContainer .collection-grid-container')?.classList.toggle('loading', loading);
    document.getElementById('ProductGridContainer')?.setAttribute('aria-busy', String(loading));
    document.querySelectorAll('[data-qf-show-results]').forEach(button => {
      button.disabled = loading;
      button.setAttribute('aria-busy', String(loading));
    });
    if (loading) document.querySelectorAll('[data-qf-filter-error]').forEach(error => { error.hidden = true; });
  }

  static async renderPage(searchParams, event, updateURL = true) {
    this.cancelPending();
    this.lastAttempt = searchParams;
    const requestId = this.requestId;
    const controller = new AbortController();
    this.controller = controller;
    this.setLoading(true);
    try {
      const section = document.getElementById('product-grid')?.dataset.id ||
        document.querySelector('facet-filters-form[data-section-id]')?.dataset.sectionId;
      if (!section) throw new Error('Missing collection section');
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(section)}&${searchParams}`;
      let html = this.filterData.find(item => item.url === url)?.html;
      if (!html) {
        const response = await fetch(url, {signal: controller.signal});
        if (!response.ok) throw new Error(`Filter response ${response.status}`);
        html = await response.text();
      }
      if (requestId !== this.requestId) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (!parsed.getElementById('ProductGridContainer')) throw new Error('Missing filtered results');
      this.renderFilters(parsed);
      this.renderProductGridContainer(parsed);
      this.renderProductCount(parsed);
      this.filterData = this.filterData.filter(item => item.url !== url).concat({url, html}).slice(-20);
      this.searchParamsPrev = searchParams;
      if (updateURL && searchParams !== window.location.search.slice(1)) {
        history.pushState({searchParams}, '', `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`);
      }
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.requestId) return;
      document.querySelectorAll('[data-qf-filter-error]').forEach(message => { message.hidden = false; });
    } finally {
      if (requestId === this.requestId) this.setLoading(false);
    }
  }

  static renderProductGridContainer(parsed) {
    const target = document.getElementById('ProductGridContainer');
    target.innerHTML = parsed.getElementById('ProductGridContainer').innerHTML;
    target.querySelectorAll('.scroll-trigger').forEach(element => element.classList.add('scroll-trigger--cancel'));
  }

  static renderFilters(parsed) {
    const activeId = document.activeElement?.id;
    document.querySelectorAll('facet-filters-form form').forEach(form => {
      const source = parsed.getElementById(form.id);
      if (!source) return;
      const drawer = form.querySelector('menu-drawer');
      const nextDrawer = source.querySelector('menu-drawer');
      if (Boolean(drawer) !== Boolean(nextDrawer)) {
        if (drawer) {
          drawer.instantlyHideDrawer?.();
          if (!document.querySelector('.drawer__container.menu-opening')) {
            document.getElementById('header')?.classList.remove('menu-open');
          }
        }
        form.innerHTML = source.innerHTML;
        if (drawer) {
          const heading = document.querySelector('main h1');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus({preventScroll: true});
        }
      }
      const accordion = form.querySelector('.facets__accordion');
      const nextAccordion = source.querySelector('.facets__accordion');
      if (accordion && nextAccordion) {
        const states = new Map(Array.from(accordion.querySelectorAll('[data-qf-facet]'), element =>
          [element.dataset.qfFacet, element.open]));
        accordion.innerHTML = nextAccordion.innerHTML;
        accordion.querySelectorAll('[data-qf-facet]').forEach(element => {
          const opened = states.has(element.dataset.qfFacet) ? states.get(element.dataset.qfFacet) : element.open;
          element.open = opened;
          element.classList.toggle('is-active', opened);
          const summary = element.querySelector('summary');
          const body = element.querySelector('.accordion__body');
          summary.setAttribute('role', 'button');
          summary.setAttribute('aria-expanded', String(opened));
          summary.setAttribute('aria-controls', body.id);
          body.style.height = opened ? 'auto' : '0px';
        });
      }
      const currentSort = form.querySelector('[name="sort_by"]');
      const nextSort = source.querySelector('[name="sort_by"]');
      if (currentSort && nextSort) {
        currentSort.value = nextSort.value;
        const sortContainer = form.querySelector('custom-select');
        const sourceSortContainer = source.querySelector('custom-select');
        if (sortContainer && sourceSortContainer) sortContainer.replaceWith(sourceSortContainer.cloneNode(true));
      }
      const button = form.querySelector('[data-qf-show-results]');
      const nextButton = source.querySelector('[data-qf-show-results]');
      if (button && nextButton) button.textContent = nextButton.textContent;
      const clear = form.querySelector('[data-qf-clear-filters]');
      const nextClear = source.querySelector('[data-qf-clear-filters]');
      if (clear && nextClear) clear.href = nextClear.getAttribute('href');
    });
    const activeFacets = document.querySelector('.active-facets');
    const nextActiveFacets = parsed.querySelector('.active-facets');
    if (activeFacets && nextActiveFacets) activeFacets.innerHTML = nextActiveFacets.innerHTML;
    if (activeId) document.getElementById(activeId)?.focus({preventScroll: true});
  }

  static renderProductCount(parsed) {
    const resultsCount = parsed.getElementById('ProductGridContainer')?.dataset.qfResultsCount;
    if (resultsCount !== undefined) {
      const grid = document.getElementById('ProductGridContainer');
      if (grid) grid.dataset.qfResultsCount = resultsCount;
      document.querySelectorAll('[data-qf-search-count]').forEach(element => {
        element.textContent = resultsCount;
      });
    }
    const count = parsed.getElementById('ProductCount');
    if (!count) return;
    ['ProductCount', 'ProductCountDesktop'].forEach(id => {
      const target = document.getElementById(id);
      if (target) { target.innerHTML = count.innerHTML; target.classList.remove('loading'); }
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const params = new URLSearchParams(window.location.search);
    for (const key of Array.from(params.keys())) {
      if (key.startsWith('filter.') || key === 'page' || key === 'section_id' || key === 'sort_by') params.delete(key);
    }
    document.querySelectorAll('facet-filters-form form').forEach(form => {
      new FormData(form).forEach((value, key) => {
        if (!value && key.startsWith('filter.')) return;
        if (key.startsWith('filter.') && !key.startsWith('filter.v.price.')) {
          if (!params.getAll(key).includes(value)) params.append(key, value);
        } else params.set(key, value);
      });
    });
    FacetFiltersForm.renderPage(params.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const link = event.currentTarget;
    let url = new URL(link.href, window.location.href);
    if (link.hasAttribute('data-qf-size-remove')) {
      // Related filters can remove size aliases from the returned facet values.
      // Remove the complete known family from the URL, including keyboard activation.
      const families = {
        L: ['L', 'Taille L'],
        XL: ['XL', 'Taille XL', 'gid://shopify/FilterSettingGroup/275054939'],
        XXL: ['XXL', '2XL', 'Taille XXL', 'gid://shopify/FilterSettingGroup/275087707'],
        '3XL': ['3XL', 'XXXL', 'Taille XXXL', 'Taille 3XL', 'gid://shopify/FilterSettingGroup/403210587']
      };
      const aliases = new Set([...link.dataset.qfSizeRemove.split('|'), ...(families[link.dataset.qfSizeLabel] || [])]);
      url = new URL(window.location.href);
      const param = 'filter.v.option.taille';
      const remaining = url.searchParams.getAll(param).flatMap(value => value.split(',')).filter(value => !aliases.has(value));
      url.searchParams.delete(param);
      url.searchParams.delete('page');
      remaining.forEach(value => url.searchParams.append(param, value));
    }
    FacetFiltersForm.renderPage(url.search.slice(1), event);
  }
}
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement{constructor(){super();this.querySelectorAll('input').forEach((element)=>{element.addEventListener('change',this.onRangeChange.bind(this));element.addEventListener('keydown',this.onKeyDown.bind(this))});this.setMinAndMaxValues()}
onRangeChange(event){this.adjustToValidValues(event.currentTarget);this.setMinAndMaxValues()}
onKeyDown(event){const allowedKeys=['Backspace','Tab','Enter','ArrowUp','ArrowDown','ArrowLeft','ArrowRight','Delete','Escape'];if(!allowedKeys.includes(event.key)&&!/[0-9.,]/.test(event.key)){event.preventDefault()}}
setMinAndMaxValues(){const inputs=this.querySelectorAll('input');const minInput=inputs[0];const maxInput=inputs[1];if(maxInput.value){minInput.setAttribute('data-max',maxInput.value)}else{minInput.setAttribute('data-max',maxInput.getAttribute('max'))}
if(minInput.value){maxInput.setAttribute('data-min',minInput.value)}else{maxInput.setAttribute('data-min',minInput.getAttribute('min'))}}
adjustToValidValues(input){const value=Number(input.value);const min=Number(input.getAttribute('min'))||0;const max=Number(input.getAttribute('max'));if(!isNaN(min)&&value<min)input.value=min;if(!isNaN(max)&&value>max)input.value=max}}
customElements.define('price-range',PriceRange);class FacetRemove extends HTMLElement{constructor(){super();const facetLink=this.querySelector('a');facetLink.setAttribute('role','button');facetLink.addEventListener('click',this.closeFilter.bind(this));facetLink.addEventListener('keyup',(event)=>{event.preventDefault();if(event.code.toUpperCase()==='SPACE')this.closeFilter(event);})}
closeFilter(event){event.preventDefault();const form=this.closest('facet-filters-form')||document.querySelector('facet-filters-form');form.onActiveFilterClick(event)}}
customElements.define('facet-remove',FacetRemove);
